import { useRef, useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { GripVertical } from 'lucide-react';
import { usePublicRestaurant } from '@/features/storefront/hooks/usePublicRestaurant';

const HINT_KEY = 'whatsapp-fab-hint-seen';

interface WhatsAppFabProps {
  restaurantId: string;
}

const STORAGE_KEY = 'whatsapp-fab-pos';
const SIZE = 48; // px — matches w-12 h-12
const DRAG_THRESHOLD = 6; // px — below this = tap, above = drag

function clamp(value: number, min: number, max: number) {
  return Math.min(Math.max(value, min), max);
}

function loadPos(): { x: number; y: number } | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function savePos(pos: { x: number; y: number }) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(pos));
  } catch {
    // ignore
  }
}

function defaultPos(): { x: number; y: number } {
  return {
    x: window.innerWidth - SIZE - 16,
    y: window.innerHeight - SIZE - (window.innerWidth < 768 ? 96 : 24),
  };
}

export function WhatsAppFab({ restaurantId }: WhatsAppFabProps) {
  const { t } = useTranslation();
  const { restaurant } = usePublicRestaurant(restaurantId);

  const [pos, setPos] = useState<{ x: number; y: number }>(() => loadPos() ?? defaultPos());
  const [dragging, setDragging] = useState(false);
  // Show "drag to move" hint on first visit; auto-dismiss after 3 s
  const [showHint, setShowHint] = useState(() => !localStorage.getItem(HINT_KEY));
  useEffect(() => {
    if (!showHint) return;
    const t = setTimeout(() => {
      setShowHint(false);
      localStorage.setItem(HINT_KEY, '1');
    }, 3000);
    return () => clearTimeout(t);
  }, [showHint]);

  const dragState = useRef<{
    startX: number;
    startY: number;
    originX: number;
    originY: number;
    moved: boolean;
  } | null>(null);

  // Clamp inside viewport whenever window resizes
  useEffect(() => {
    const onResize = () => {
      setPos((p) => ({
        x: clamp(p.x, 0, window.innerWidth - SIZE),
        y: clamp(p.y, 0, window.innerHeight - SIZE),
      }));
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const onPointerDown = useCallback((e: React.PointerEvent) => {
    e.currentTarget.setPointerCapture(e.pointerId);
    dragState.current = {
      startX: e.clientX,
      startY: e.clientY,
      originX: pos.x,
      originY: pos.y,
      moved: false,
    };
  }, [pos]);

  const onPointerMove = useCallback((e: React.PointerEvent) => {
    if (!dragState.current) return;
    const dx = e.clientX - dragState.current.startX;
    const dy = e.clientY - dragState.current.startY;
    if (!dragState.current.moved && Math.hypot(dx, dy) < DRAG_THRESHOLD) return;
    dragState.current.moved = true;
    setDragging(true);
    setPos({
      x: clamp(dragState.current.originX + dx, 0, window.innerWidth - SIZE),
      y: clamp(dragState.current.originY + dy, 0, window.innerHeight - SIZE),
    });
  }, []);

  const onPointerUp = useCallback((e: React.PointerEvent) => {
    if (!dragState.current) return;
    const wasDrag = dragState.current.moved;
    dragState.current = null;
    setDragging(false);
    if (wasDrag) {
      // Persist final position
      setPos((p) => { savePos(p); return p; });
      // Suppress the click that fires after pointerup
      e.currentTarget.addEventListener('click', (ev) => ev.preventDefault(), { once: true });
    }
  }, []);

  if (!restaurant) return null;

  const raw = restaurant.socialLinks?.whatsapp || restaurant.contactPhone;
  if (!raw) return null;

  const href = raw.startsWith('http') ? raw : `https://wa.me/${raw.replace(/\D/g, '')}`;

  return (
    <div
      style={{ left: pos.x, top: pos.y, touchAction: 'none' }}
      className="fixed z-40 select-none"
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
    >
      {/* "Drag to move" hint chip — auto-fades after 3 s on first visit */}
      <div
        className={`absolute -top-9 left-1/2 -translate-x-1/2 whitespace-nowrap flex items-center gap-1 px-2 py-1 rounded-full bg-zinc-800/90 text-white text-[10px] font-medium shadow-lg pointer-events-none transition-all duration-500 ${
          showHint && !dragging ? 'opacity-100 -translate-y-0' : 'opacity-0 translate-y-1'
        }`}
      >
        <GripVertical className="w-3 h-3 opacity-70" />
        {t('landing.whatsappDragHint')}
      </div>

      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={t('landing.whatsappAriaLabel')}
        draggable={false}
        onClick={(e) => { if (dragging) e.preventDefault(); }}
      >
        {!dragging && (
          <span className="absolute inset-0 rounded-full bg-[#25D366] opacity-30 animate-ping" aria-hidden="true" />
        )}
        <span
          className={`relative flex w-12 h-12 rounded-full bg-[#25D366] shadow-lg shadow-green-500/40 items-center justify-center transition-all duration-150 ${
            dragging
              ? 'scale-110 shadow-xl shadow-green-500/60 cursor-grabbing'
              : 'hover:scale-110 hover:shadow-xl hover:shadow-green-500/50 active:scale-95 cursor-grab'
          }`}
        >
          {/* Grip dots on the edge — always visible as a subtle drag affordance */}
          <span className="absolute top-1 right-1 flex flex-col gap-[2.5px] opacity-60">
            <span className="flex gap-[2.5px]">
              <span className="w-[3px] h-[3px] rounded-full bg-white" />
              <span className="w-[3px] h-[3px] rounded-full bg-white" />
            </span>
            <span className="flex gap-[2.5px]">
              <span className="w-[3px] h-[3px] rounded-full bg-white" />
              <span className="w-[3px] h-[3px] rounded-full bg-white" />
            </span>
          </span>

          <svg viewBox="0 0 24 24" className="w-6 h-6 fill-white" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
          </svg>
        </span>
      </a>
    </div>
  );
}
